import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class PasswordSecurityService {
  constructor(private http: HttpClient) {}

  /**
   * Checks password strength based on validation rules
   * Matches password-validators.ts exactly
   */
  checkPasswordStrength(password: string): {
    strength: 'weak' | 'medium' | 'strong' | 'very-strong';
    score: number;
    feedback: string[];
  } {
    let score = 0;
    const feedback: string[] = [];

    if (!password) {
      return { strength: 'weak', score: 0, feedback: [] };
    }

    // Validation conditions matching password-validators.ts exactly
    const isMinLengthValid = password.length >= 8;
    const isMaxLengthValid = password.length <= 20;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!"#$%&'()*+,\-./:;<=>?@[\]^_{}|~]/.test(password);
    const hasBackslash = /\\/.test(password);
    const hasLeadingOrTrailingSpace = /^\s|\s$/.test(password);

    // Check for common passwords first
    const isCommon = this.isCommonPassword(password);
    if (isCommon) {
      feedback.push('Avoid common passwords');
    }

    // Length checks
    if (isMinLengthValid) {
      score += 1;
      // Bonus for longer passwords (12+ characters)
      if (password.length >= 10) {
        score += 1;
      }
      if (password.length >= 14) {
        score += 1;
      }
    } else {
      feedback.push('Use at least 8 characters');
    }

    // Penalty for exceeding max length
    if (!isMaxLengthValid) {
      score = Math.max(0, score - 1);
      feedback.push('Use no more than 20 characters');
    }

    // Character variety checks
    if (hasUppercase) {
      score += 1;
    } else {
      feedback.push('Add uppercase letters');
    }

    if (hasLowercase) {
      score += 1;
    } else {
      feedback.push('Add lowercase letters');
    }

    if (hasNumber) {
      score += 1;
    } else {
      feedback.push('Add numbers');
    }

    // Special character check (matching validator: specific set, no backslash)
    if (hasSpecialChar && !hasBackslash) {
      score += 2;
    } else {
      if (hasBackslash) {
        feedback.push('Remove backslash characters');
      } else {
        feedback.push('Add special characters (not except backslash)');
      }
    }

    // Leading/trailing space check
    if (hasLeadingOrTrailingSpace) {
      score = Math.max(0, score - 1);
      feedback.push('No leading or trailing spaces');
    }

    // Determine strength based on score
    // Max possible score: 6 points (1 min length + 1 bonus 12+ + 1 uppercase + 1 lowercase + 1 number + 1 special)
    let strength: 'weak' | 'medium' | 'strong' | 'very-strong';
    
    // Common passwords are always weak, regardless of other factors
    if (isCommon) {
      strength = 'weak';
      score = 0;
    } else if (score <= 5) {
      strength = 'weak';
    } else if (score <= 6) {
      strength = 'medium';
    } else if (score <= 7) {
      strength = 'strong';
    } else {
      strength = 'very-strong';
    }
    console.warn("checking ", score, strength);
    return { strength, score, feedback };
  }

  /**
   * Checks if password is in the list of common passwords
   * Simple includes check - case insensitive
   */
  private isCommonPassword(password: string): boolean {
    if (!password || password.length === 0) {
      return false;
    }

    // List of common passwords
    const commonPasswordsList = [
      // Top most common
      '123456', 'password', '123456789', '12345678', '12345',
      '1234567', '1234567890', 'qwerty', 'abc123', '111111',
      
      // Common variations
      'password1', 'password12', 'password123', 'password1234',
      '123456a', '123456789a', 'qwerty123', 'qwertyuiop',
      'abc1234', 'abcd1234', 'abc12345',
      
      // Simple sequences
      '1234', '123', '0123456789',
      
      // Common words
      'admin', 'root', 'guest', 'user', 'test',
      'welcome', 'login', 'letmein', 'master', 'sunshine',
      'princess', 'shadow', 'monkey', 'dragon', 'baseball',
      'football', 'iloveyou', 'trustno1', 'superman', 'batman',
      'michael', 'jordan', 'tigger', 'harley', 'hunter',
      'buster', 'thomas', 'hockey', 'ranger', 'daniel',
      'hannah', 'maggie', 'jessica', 'charlie', 'jennifer',
      'joshua', 'amanda', 'justin', 'sarah', 'matthew',
      
      // Keyboard patterns
      'asdf', 'asdfgh', 'zxcv', 'zxcvbn', 'asdf123', 'zxcv123',
      
      // Simple patterns
      'aaaaaa', 'aaaaaaa', 'aaaaaaaa', '1111111',
      '000000', '0000000', '123321', '654321', '112233'
    ];

    // Simple check: if password (lowercase) is in the list, it's common
    return commonPasswordsList.includes(password.toLowerCase());
  }

  /**
   * Checks if password has been found in data breaches using Have I Been Pwned API
   * Uses k-anonymity protocol for security
   */
  async checkPasswordBreach(password: string): Promise<boolean> {
    try {
      const hash = await this.sha1(password);
      const prefix = hash.substring(0, 5);
      const suffix = hash.substring(5);

      const response = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`);
      const hashes = await response.text();
      
      return hashes.includes(suffix.toUpperCase());
    } catch {
      return false; // If check fails, allow password
    }
  }

  /**
   * Generates SHA-1 hash of the password
   */
  private async sha1(message: string): Promise<string> {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-1', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }
}
